import { IamGroupType } from '@navio/nsdk';
import { type InputData, noddiAsync, URLKeys } from 'noddi-async';
import { filterUnitFor, useFilterSpecValue, useFilterUnits } from 'noddi-ui-web';
import { useBrandOptions } from '../../hooks/filters/Brand/useBrandFilter';
import {
  SERVICE_DEPARTMENT_FILTER_TEXT,
  useServiceDepartmentOptions
} from '../../hooks/filters/ServiceDepartment/useServiceDepartmentFilter';
import {
  SERVICE_ORGANIZATION_FILTER_TEXT,
  useServiceOrganizationOptions
} from '../../hooks/filters/ServiceOrganization/useServiceOrganizationFilter';
import { resolveImpersonationServiceOrganizationIds } from './resolveImpersonationServiceOrganizationIds';
import { useImpersonationRoleOptions } from './useImpersonationRoleOptions';
import {
  impersonationDataQualityFilters,
  organizationImpersonationFilterUnit,
  useImpersonationUserFilters,
  userImpersonationFilterUnit
} from './useImpersonationUserFilters';
import { useImpersonationUserGroupScope } from './useImpersonationUserGroupScope';

// Brands and roles scope the users through the service organization and IAM policy lookups: their builders are
// bound to those endpoints, and the user list gets what the lookups resolve to.
const brandScopeUnit = filterUnitFor<NonNullable<InputData<typeof URLKeys.getPaginatedServiceOrganizations>>>();
const roleScopeUnit =
  filterUnitFor<NonNullable<InputData<typeof URLKeys.getPaginatedIamPoliciesIgnoringImpersonation>>>();

/**
 * The impersonation dialog's filters: both tabs' units, and the scope brand, organization, department, role and
 * data quality resolve the user list to. Call it inside the dialog's local query param scope.
 */
export const useImpersonationScopeFilters = ({
  defaultBrandDomain,
  searchQuery
}: {
  /** Customer-app brand domain (e.g. `bestdrive`) — pre-selects that brand and scopes users. */
  defaultBrandDomain?: string;
  searchQuery: string;
}) => {
  const { userFilters, organizationFilters } = useImpersonationUserFilters();

  const {
    data: defaultBrand,
    isPending: isDefaultBrandPending,
    error: defaultBrandError
    // eslint-disable-next-line no-restricted-properties -- the brand default selection scopes the user list request
  } = noddiAsync.useGet({
    type: URLKeys.getByDomainBrand,
    input: { brandDomain: defaultBrandDomain! },
    queryConfig: { enabled: !!defaultBrandDomain }
  });

  // The domain's brand is the default once it loads; a brand the user clears stays cleared.
  const brandFilter = brandScopeUnit.multiSelect('brandIds', {
    title: 'Brands',
    description: 'Filter by specific brands',
    source: useBrandOptions(),
    defaults: defaultBrand?.id != null ? [defaultBrand.id] : undefined,
    // A brand only widens to its service organizations; an excluded brand has no organization scope to send.
    exclude: false
  });
  const brandIds = useFilterSpecValue(brandFilter).brandIds ?? [];
  const brandScope = brandIds.length > 0 ? brandIds : undefined;

  const serviceOrganizationFilter = userImpersonationFilterUnit.multiSelect('serviceOrganizationIds', {
    ...SERVICE_ORGANIZATION_FILTER_TEXT,
    source: useServiceOrganizationOptions({ brandIds: brandScope }),
    exclude: true
  });
  const serviceOrganizationIds = useFilterSpecValue(serviceOrganizationFilter).serviceOrganizationIds ?? [];
  const serviceDepartmentFilter = userImpersonationFilterUnit.multiSelect('serviceDepartmentIds', {
    ...SERVICE_DEPARTMENT_FILTER_TEXT,
    source: useServiceDepartmentOptions({
      serviceOrganizationIds: serviceOrganizationIds.length > 0 ? serviceOrganizationIds : undefined,
      brandIds: brandScope
    }),
    exclude: true
  });
  const serviceDepartmentIds = useFilterSpecValue(serviceDepartmentFilter).serviceDepartmentIds ?? [];
  const roleFilter = roleScopeUnit.multiSelect('roleIds', {
    title: 'IAM roles',
    source: useImpersonationRoleOptions(),
    // Roles resolve to the user groups their policies name; an excluded role names no such set.
    exclude: false
  });
  const selectedRoleIds = useFilterSpecValue(roleFilter).roleIds ?? [];
  const dataQualityInput = {
    ...useFilterSpecValue(impersonationDataQualityFilters.hasIssues),
    ...useFilterSpecValue(impersonationDataQualityFilters.issueTypes)
  };

  // When a brand is selected but no org is, resolve all orgs for that brand so the user list is brand-scoped.
  // User list API has no brandIds filter.
  const {
    data: brandServiceOrganizations,
    isPending: isBrandOrgsPending,
    error: brandOrgsError
    // eslint-disable-next-line no-restricted-properties -- the brand's organizations are the user list's scope
  } = noddiAsync.useGet({
    type: URLKeys.getPaginatedServiceOrganizations,
    // Every org of the brand must come back: the ids below are the whole brand scope, so a
    // truncated page would silently narrow the user list. Unpaginated is what guarantees that
    // -- the API returns every row up to its cap and rejects the request above it.
    input: { brandIds },
    // No placeholderData: stale orgs from a previous brand must not scope the user list.
    queryConfig: {
      enabled: brandIds.length > 0 && serviceOrganizationIds.length === 0
    }
  });

  const effectiveServiceOrganizationIds = resolveImpersonationServiceOrganizationIds({
    selectedServiceOrganizationIds: serviceOrganizationIds,
    brandIds,
    brandServiceOrganizationIds: brandServiceOrganizations?.results.map((org) => org.id),
    search: searchQuery
  });

  // Wait for domain→brand seed, then brand→orgs resolution so the table never flashes unfiltered users.
  // A typed search skips brand scope, so do not block the list on that resolution. The seed is the brand
  // filter's default, so it applies in the render the brand loads.
  const isWaitingForDefaultBrandSeed = !searchQuery && !!defaultBrandDomain && isDefaultBrandPending;
  const isWaitingForBrandOrgs =
    !searchQuery &&
    brandIds.length > 0 &&
    serviceOrganizationIds.length === 0 &&
    (isBrandOrgsPending || !brandServiceOrganizations);
  const isWaitingForBrandScope = isWaitingForDefaultBrandSeed || isWaitingForBrandOrgs;
  const userGroupScope = useImpersonationUserGroupScope({
    selectedRoleIds,
    dataQualityInput,
    effectiveServiceOrganizationIds,
    isEnabled: !isWaitingForBrandScope,
    serviceDepartmentIds
  });

  // Either scope resolving to zero ids means nothing can match. The backend validates these
  // filters against real rows, so there is no "matches nothing" id to send -- the request is
  // skipped and the table renders empty instead.
  const hasEmptyScope = effectiveServiceOrganizationIds?.length === 0 || userGroupScope.hasNoMatches;

  const usersFilters = useFilterUnits([
    // The user list has no brand param: a brand narrows the organization scope below, so it writes nothing itself.
    userImpersonationFilterUnit.map([brandFilter], () => ({})),
    // The organizations the brand and search resolve to, not only the picked ones.
    userImpersonationFilterUnit.map([serviceOrganizationFilter], (params) => ({
      ...params,
      serviceOrganizationIds: effectiveServiceOrganizationIds
    })),
    serviceDepartmentFilter,
    // Role and data quality (excluded issue types included) resolve through the user group lookup to these ids.
    userImpersonationFilterUnit.lookup(
      [roleFilter, impersonationDataQualityFilters.hasIssues, impersonationDataQualityFilters.issueTypes],
      { userGroupIds: userGroupScope.userGroupIds }
    ),
    ...userFilters
  ]);
  const organizationsFilters = useFilterUnits([
    organizationImpersonationFilterUnit.locked({ types: [IamGroupType.ORGANIZATION] }),
    ...organizationFilters
  ]);

  return {
    usersFilters,
    organizationsFilters,
    serviceDepartmentIds,
    userGroupScope,
    isWaitingForBrandScope,
    hasEmptyScope,
    error: defaultBrandError ?? brandOrgsError
  };
};
